import { useEffect, useMemo } from 'react'
import { DeleteIcon } from '@chakra-ui/icons'
import { Alert, AlertIcon, AlertTitle, Box, BoxProps, IconButton } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'

import { DataTable } from '@components/DataTable/DataTable'

import { useSearchState } from '@hooks/useSearchState'

import { EnrichedMatchEditorApiEvent, MatchEventEditorEventType } from '@common/types'

import { useMatchEditorAlertDialogContext } from '@contexts/MatchEditorAlertDialogContext'
import { useSelectedEventContext } from '@contexts/SelectedEventContext'

import { useMatchEventEditorData } from '@queries/useMatchEventEditorData'

import { getMatchEditorEventTypeLabel, getMatchEditorFixtureStatusLabel, matchEventSortFn } from './utils'

const columnHelper = createColumnHelper<EnrichedMatchEditorApiEvent>()

const KEY_EVENT_TYPES: number[] = [
    MatchEventEditorEventType.goal,
    MatchEventEditorEventType.yellow_card,
    MatchEventEditorEventType.yellowred_card,
    MatchEventEditorEventType.red_card,
    MatchEventEditorEventType.video_assistant_referee_over,
    MatchEventEditorEventType.penalty_shootout_event_deep_coverage,
    MatchEventEditorEventType.penalty_missed,
    MatchEventEditorEventType.substitution,
]

type ActionCellProps = {
    event: EnrichedMatchEditorApiEvent
} & BoxProps

export function ActionCell({ event, ...props }: ActionCellProps) {
    const { remove } = useMatchEventEditorData()

    const { onOpen, setOnCloseCallback } = useMatchEditorAlertDialogContext()

    return (
        <Box
            as="span"
            display="flex"
            justifyContent="space-around"
            alignItems="center"
            gap={4}
            w="100%"
            position="relative"
            h="24px"
            borderRadius="16"
            {...props}>
            {event.isRemovable && (
                <IconButton
                    flex={1}
                    colorScheme="red"
                    aria-label="Delete event"
                    size="xs"
                    position="absolute"
                    top="0"
                    right="0"
                    onClick={() => {
                        setOnCloseCallback(() => remove(event.id))

                        onOpen()
                    }}
                    icon={<DeleteIcon />}
                />
            )}
        </Box>
    )
}

export function EventTable({ showAllEvents }: { showAllEvents: boolean }) {
    const { events } = useMatchEventEditorData()
    const { selectedEvent, setSelectedEvent } = useSelectedEventContext()
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_, setSearchState] = useSearchState()

    const handleRowClick = (rowEvent: EnrichedMatchEditorApiEvent) => {
        if (rowEvent.isEditable || rowEvent.isRemovable) {
            setSelectedEvent(rowEvent)
        }
    }

    useEffect(() => {
        if (events && events.length > 0) {
            const updatedEvent = events.find(event => event.id === selectedEvent?.id)

            if (updatedEvent) {
                setSelectedEvent(updatedEvent)
            }

            if (!events?.length) {
                setSearchState({ eventId: undefined })
            }
        }
    }, [events, selectedEvent, setSearchState, setSelectedEvent])

    const columns = useMemo(
        () => [
            columnHelper.accessor('event_time', {
                id: 'match_time',
                cell: value => value.getValue(),
                header: 'Time',
                sortingFn: (a, b) => matchEventSortFn(a.original, b.original),
            }),
            columnHelper.accessor('team_name', {
                cell: value => {
                    const isMatchEvent = value.row.original.type === MatchEventEditorEventType.fixture_status
                    if (isMatchEvent) {
                        return getMatchEditorEventTypeLabel(value.row.original.type)
                    }

                    return (
                        <Box as="span" isTruncated={true} maxInlineSize="200px">
                            {value.getValue()}
                        </Box>
                    )
                },
                header: 'Team',
            }),
            columnHelper.accessor('type', {
                cell: value => {
                    const isGoal = value.getValue() === MatchEventEditorEventType.goal
                    const isMatchEvent = value.getValue() === MatchEventEditorEventType.fixture_status

                    if (isGoal) {
                        return (
                            <Box>
                                {getMatchEditorEventTypeLabel(value.getValue())} ({value.row.original.score?.team1}:
                                {value.row.original.score?.team2})
                            </Box>
                        )
                    }

                    if (isMatchEvent) {
                        return <Box>{getMatchEditorFixtureStatusLabel(value.row.original.extra_info)}</Box>
                    }

                    return <Box minInlineSize="120px">{getMatchEditorEventTypeLabel(value.getValue())}</Box>
                },
                header: 'Event',
            }),
            columnHelper.display({
                id: 'actions',
                meta: {
                    showOnHover: true,
                } as any,
                cell: cell => <ActionCell event={cell.row.original} />,
            }),
        ],
        [],
    )

    if (!events?.length) {
        return (
            <Alert status="info">
                <AlertIcon />
                <AlertTitle>No events to edit!</AlertTitle>
                Add new event or select a different match.
            </Alert>
        )
    }

    let filteredEvents = events

    if (!showAllEvents) {
        filteredEvents = events.filter(e => KEY_EVENT_TYPES.includes(Number(e.type)))
    }

    return (
        <Box overflowY="auto" height="100%">
            <DataTable<EnrichedMatchEditorApiEvent>
                columns={columns}
                data={filteredEvents}
                onRowClick={handleRowClick}
                selectedRow={selectedEvent?.id}
                enableRowHover
            />
        </Box>
    )
}
